<template>
  <v-dialog
    :model-value="true"
    scrollable
    persistent
    max-width="1536px"
  >
    <v-card
      id="ab-test-simulation"
      :loading="isLoading || isSubmitting"
    >
      <v-card-title>
        <span>A/B Waterfall Test</span>

        <v-spacer />

        <v-btn
          icon
          @click="handleClose"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <div
        v-if="isLoading"
        class="d-flex flex-column align-center py-4 px-4"
      >
        <h3 class="mb-6 mt-2">
          Please wait while we load the test data...
        </h3>
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        />
      </div>
      <template v-else>
        <v-card-text class="mt-3">
          <v-stepper
            v-model="activeStep"
            :flat="true"
            non-linear
          >
            <v-stepper-header>
              <v-stepper-item
                :complete="activeStep > 1"
                :value="1"
                :editable="editableStep"
                title="Test Lead Data"
              />

              <v-divider />

              <v-stepper-item
                :complete="activeStep > 2"
                :value="2"
                :editable="editableStep"
                title="Clarity Data"
              />

              <v-divider />

              <v-stepper-item
                :complete="activeStep > 3"
                :value="3"
                :editable="editableStep"
                title="Validifi Data"
              />

              <v-divider />

              <v-stepper-item
                :complete="activeStep > 4"
                :value="4"
                :editable="editableStep"
                title="White Pages Data"
              />

              <v-divider />

              <v-stepper-item
                :complete="activeStep > 5"
                :value="5"
                :editable="editableStep"
                title="Submit"
              />
            </v-stepper-header>

            <v-stepper-window>
              <!-- Test Lead Data -->
              <v-stepper-window-item :value="1">
                <div class="my-3">
                  <WaterfallTestLeadDataForm
                    v-model:lead-data="leadData"
                    :campaigns="campaigns"
                    :lead-providers="leadProviders"
                    :us-states="usStates"
                    :customers="customers"
                  />
                </div>
              </v-stepper-window-item>
              <!-- ./Test Lead Data -->

              <!--   Clarity Data -->
              <v-stepper-window-item :value="2">
                <div class="my-3">
                  <WaterfallTestClarityDataForm :clarity-data="leadData.clarityResponse" />
                </div>
              </v-stepper-window-item>
              <!--   Clarity Data -->

              <!--   Validifi Data -->
              <v-stepper-window-item :value="3">
                <div class="my-3">
                  <WaterfallTestValidifiDataForm
                    :lead-data="leadData"
                    :campaigns="campaigns"
                    :validifi-data="leadData.validifiResponse"
                  />
                </div>
              </v-stepper-window-item>
              <!-- ./Validifi Data -->

              <!--  White Pages Data -->
              <v-stepper-window-item :value="4">
                <div class="my-3">
                  <WaterfallTestWhitePagesFormData :white-pages-data="leadData.whitepagesResponse" />
                </div>
              </v-stepper-window-item>
              <!-- ./White Pages Data -->

              <!--  Finalize and Submit -->
              <v-stepper-window-item :value="5">
                <div class="my-3">
                  <AbWaterfallTestResult
                    :lead-data="leadData"
                    @submitting="isSubmitting = $event"
                  />
                </div>
              </v-stepper-window-item>
              <!-- ./Finalize and Submit -->
            </v-stepper-window>
          </v-stepper>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { mapActions } from 'vuex';
import { formatPercentage } from '@/filters.js';
import WaterfallTestLeadDataForm from '@/views/Waterfall/WaterfallTestLeadDataForm.vue';
import WaterfallTestClarityDataForm from '@/views/Waterfall/WaterfallTestClarityDataForm.vue';
import WaterfallTestValidifiDataForm from '@/views/Waterfall/WaterfallTestValidifiDataForm.vue';
import WaterfallTestWhitePagesFormData from '@/views/Waterfall/WaterfallTestWhitePagesFormData.vue';
import stateList from '@/data/states.json';
import { generateQaData } from '@/views/Waterfall/qaData.js';
import AbWaterfallTestResult from './AbWaterfallTestResult.vue';
</script>

<script>
export default {
  name: 'AbWaterfallTest',

  props: {
    abBuckets: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['close'],

  data() {
    return {
      isLoading: true,
      isSubmitting: false,

      activeStep: 1,
      errorMessages: [],
      customers: [],
      campaigns: [],
      leadProviders: [],
      usStates: [],
      leadData: {},
    };
  },

  computed: {
    hasErrors() {
      return this.errorMessages.length > 0;
    },

    editableStep() {
      return !this.isSubmitting;
    },
  },

  async created() {
    await this.initializeComponent();
  },

  methods: {
    formatPercentage,
    ...mapActions('notification', ['setNotification']),

    async initializeComponent() {
      this.initializeData();
      await this.loadTestData();
    },

    initializeData() {
      this.leadData = { ...generateQaData() };
      this.usStates = (stateList || []).map((state) => state.id);
      this.errorMessages = [];
    },

    async loadTestData() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get('/api/v1/waterfall/test');
        const { data } = response;

        this.processApiData(data);
        this.setDefaultValues();
        this.handleDataValidation();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },

    processApiData(data) {
      this.customers = this.processCustomers(data?.customers || []);
      this.campaigns = this.processCampaigns(data?.campaigns || [], data?.lead_providers || []);
      this.leadProviders = this.processLeadProviders(data?.lead_providers || []);
      this.updateLeadDataFromApi(data);
    },

    processCustomers(customers) {
      return customers.map((customer) => ({
        value: customer.guid,
        text: `${customer.firstname} ${customer.lastname}`,
        email: customer.email,
        phone: customer.homephone,
      }));
    },

    processCampaigns(campaigns, leadProviders) {
      return campaigns.map((campaign) => {
        const lp = leadProviders.find((v) => v.id === campaign.lead_provider_id);
        return {
          value: campaign.id,
          text: `${campaign.id} (${campaign.campaign_name} - ${campaign.product_type})`,
          apiKey: lp?.api_key,
          leadCategory: lp?.category || '',
        };
      });
    },

    processLeadProviders(providers) {
      return providers.map((provider) => ({
        value: provider.api_key,
        text: `${provider.company_name} (${provider.category})`,
      }));
    },

    updateLeadDataFromApi(data) {
      // Update lead data with API response
      this.leadData.lastpaydate = data.last_pay_date;
      this.leadData.nextpaydate = data.next_pay_date;
      this.leadData.secondpaydate = data.second_pay_date;
      this.leadData.address = data.address;
      this.leadData.homephone = data.homephone;
      this.leadData.ssn = data.ssn;
      this.leadData.accountnumber = data.accountnumber;
    },

    setDefaultValues() {
      // Set default campaign
      if (this.campaigns.length > 0) {
        this.leadData.campaign = this.campaigns[0].value;
      }

      // Set default lead provider
      if (this.leadProviders.length > 0) {
        this.leadData.api_key = this.leadProviders[0].value;
      }
    },

    handleDataValidation() {
      if (this.campaigns.length === 0) {
        this.addError('No Campaigns found.');
      }

      if (this.leadProviders.length === 0) {
        this.addError('No Lead Providers found.');
      }

      if (this.hasErrors) {
        this.showErrorNotification();
      }
    },

    addError(message) {
      this.errorMessages.push(message);
    },

    handleApiError(error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load test data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load test data';
      this.addError(errorMessage);

      this.setNotification({
        type: 'error',
        message: errorMessage,
      });
    },

    showErrorNotification() {
      this.setNotification({
        type: 'warning',
        message: `Data validation issues: ${this.errorMessages.join(', ')}`,
      });
    },

    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
#ab-test-simulation .v-toolbar__title,
#ab-test-simulation .v-btn__content {
  text-transform: none !important;
}

#ab-test-simulation .v-toolbar__content {
  padding-left: 0;
  padding-right: 0;
}
</style>
